import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Font,
} from "@react-pdf/renderer";
import moment from "moment";
import PieChart from "./pieChart";

// Register Open Sans font
Font.register({
  family: "Open Sans",
  fonts: [
    { src: "https://cdn.jsdelivr.net/npm/open-sans-all@0.1.3/fonts/open-sans-regular.ttf", fontWeight: "normal" },
    { src: "https://cdn.jsdelivr.net/npm/open-sans-all@0.1.3/fonts/open-sans-700.ttf", fontWeight: "bold" }
  ]
});

// Define styles
const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontFamily: "Open Sans",
  },
  section: {
    marginBottom: 15,
  },
  title: {
    fontSize: 12, // Decreased font size
    fontFamily: "Open Sans", // Must match registered name
    fontWeight: "bold", // Ensures the bold variant is used
    backgroundColor: "#0E2841",
    color: "white",
    padding: 6,
    borderRadius: 5,
    marginBottom: 10,
  },
  subtitle: {
    fontFamily: "Open Sans", // Must match registered name
    fontWeight: "bold", // Ensures the bold variant is used
    fontSize: 8, // Decreased font size
    color: "#6B7280", // Color for the label text

    marginBottom: 15,
  },
  holdingContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  holdingName: {
    fontFamily: "Open Sans", // Must match registered name
    fontWeight: "bold", // Ensures the bold variant is used
    fontSize: 8,
    color: "#0D215C",

  },
  holdingPercentage: {
    fontFamily: "Open Sans", // Must match registered name
    fontWeight: "bold", // Ensures the bold variant is used
    fontSize: 8,

    color: "#0D215C",

  },
  note: {
    fontSize: 10, // Decreased font size
    fontFamily: "Open Sans", // Must match registered name
    fontWeight: "bold", // Ensures the bold variant is used
    marginTop: 20,
    color: "#000000",
  },
   footer: {
    fontSize: 12,
    fontWeight: "bold",
    marginTop: 20,
  },
  footerLink: {
    color: "#3B82F6",
    textDecoration: "underline",
  },
});

// Date formatting function
function formatDate(dateString, fallback = 0) {
  if (!dateString) return fallback;

  // Try parsing ISO format or other recognized formats
  let parsedDate = moment(dateString, moment.ISO_8601, true);

  if (!parsedDate.isValid()) {
    // Fallback to parsing "DD.MM.YYYY" format
    parsedDate = moment(dateString, "DD.MM.YYYY", true);
  }
  if (!parsedDate.isValid()) {
    // Fallback to parsing "DD.MM.YYYY" format
    parsedDate = moment(dateString, "DD-MM-YYYY", true);
  }

  if (!parsedDate.isValid()) {
    // Fallback to parsing "DD MMM YYYY" format (e.g., "01 Jan 2020")
    parsedDate = moment(dateString, "DD MMM YYYY", true);
  }

  if (!parsedDate.isValid()) {
    // Fallback to JavaScript Date object for other formats
    const jsDate = new Date(dateString);
    if (!isNaN(jsDate.getTime())) {
      parsedDate = moment(jsDate);
    }
  }

  // Return formatted date in "DD MMM YYYY" format or fallback if invalid
  return parsedDate.isValid() ? parsedDate.format("DD MMM YYYY") : fallback;
}

// Create the PDF component
const PortfolioTemplate = ({ schemePortfolioData, schemeSectorAllocationData }) => {


  const colors = [
    '#FF6384', // Red
    '#36A2EB', // Blue
    '#FFCE56', // Yellow
    '#4BC0C0', // Teal
    '#9966FF', // Purple
    '#FF9F40', // Orange
    '#66FF66', // Green
    '#FF66B2', // Pink
    '#3399FF', // Light Blue
    '#FFCC99'  // Peach
  ]

  return (
    <View>

      {/* Portfolio Section */}
      <View style={styles.section}>
        <Text style={styles.title}>Portfolio</Text>
        <Text style={styles.subtitle}>
          Portfolio details as on {schemePortfolioData && schemePortfolioData.length > 0 && formatDate(schemePortfolioData[0]?.as_on_date)}
        </Text>
        <Text style={{ fontSize: 10, fontWeight: "bold", marginBottom: 10, fontFamily: "Open Sans" }}>Top 10 Holdings</Text>
        <View style={{ borderBottom: "1px solid #000", marginBottom: 10 }}></View>

        {/* Holdings List */}
        <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
          <View style={{ width: "48%" }}>
            {schemePortfolioData && schemePortfolioData.slice(0, 5).map((holding, index) => (
              <View key={index} style={styles.holdingContainer}>
                <Text style={styles.holdingName}>{holding.name}</Text>
                <Text style={styles.holdingPercentage}>{holding.percentage}%</Text>
              </View>
            ))}
          </View>
          <View style={{ width: "48%" }}>
            {schemePortfolioData && schemePortfolioData.slice(5, 10).map((holding, index) => (
              <View key={index} style={styles.holdingContainer}>
                <Text style={styles.holdingName}>{holding.name}</Text>
                <Text style={styles.holdingPercentage}>{holding.percentage}%</Text>
              </View>
            ))}
          </View>
        </View>
      </View>

      {/* Sectoral Allocation Section */}
      <View style={styles.section}>
        <Text style={styles.title}>Sectoral Allocation</Text>
        {/* Placeholder for Pie Chart */}
      </View>
      <PieChart data={schemeSectorAllocationData} colors={colors} />

      {/* Render the footer note */}

      <View style={styles.footer}>
        <Text style={styles.note}>
          Note: Please reach out to us on{" "}
          <Text style={styles.footerLink}>vasuki.compliance@vasukiindia.com</Text> for
          more details.
        </Text>
      </View>
    </View>
  );
};

export default PortfolioTemplate;